import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useLayoutEffect,
} from "react";
import { toast } from "react-toastify";
import {
  useGetGroupHistoryQuery,
  useGetGroupReceiptsQuery,
  useSendGroupMessageMutation,
  useGetPresenceQuery,
  useSendTypingSignalMutation,
  useMarkChatReadMutation,
  useGetGroupReactionsQuery,
  useReactGroupMessageMutation,
  useSendVoiceMessageMutation,
  useGetVoiceListenStateQuery,
  useRecordVoiceHeardMutation,
  type ReactionGroup,
} from "../../services/rtkApi/agentApi";
import { useAppSelector } from "../../store/hooks";
import { ChatMessage } from "./ChatMessage";
import { ReadReceipts, type ReceiptViewer } from "./ReadReceipts";
import { MessageReactions } from "./MessageReactions";
import { PresenceBar } from "./PresenceBar";
import { AgentStatusBadge } from "./AgentStatusBadge";
import { TypingIndicator } from "./TypingIndicator";
import { Button } from "../../ui";
import { MentionTextarea } from "./MentionTextarea";
import { VoiceRecorder } from "./VoiceRecorder";
import { ChatSendButton } from "./ChatSendButton";
import { Mic } from "lucide-react";
import { VirtualizedMessageList, type VirtualizedChatItem } from "./VirtualizedMessageList";
import { useAgentChatScroll } from "./useAgentChatScroll";
import { NewMessagesIndicator } from "./NewMessagesIndicator";
import { ChatMessagesSkeleton } from "./ChatMessagesSkeleton";
import {
  GROUP_HISTORY_LIMIT,
  GROUP_HISTORY_POLL_MS,
} from "./agentChatConstants";

const TYPING_THROTTLE_MS = 3_000;

export type GroupChatProps = {
  /** when false, skip history subscription/polling (e.g. desktop panel display:none) */
  historySubscriptionActive?: boolean;
};

export function GroupChat({
  historySubscriptionActive = true,
}: GroupChatProps) {
  const user = useAppSelector((s) => s.auth);
  const isLoggedIn = user.bootstrapStatus === "ready" && !!user.token;

  const { data: presenceData } = useGetPresenceQuery();
  const [sendTyping] = useSendTypingSignalMutation();
  const lastTypingRef = useRef(0);

  const typingUsers = useMemo(() => {
    if (!presenceData?.users) return [];
    return presenceData.users.filter(
      (u) => u.typing && u.username !== user.username,
    );
  }, [presenceData?.users, user.username]);

  const typingLabel = useMemo(() => {
    if (typingUsers.length === 0) return null;
    if (typingUsers.length === 1) return typingUsers[0].username;
    if (typingUsers.length === 2) {
      return `${typingUsers[0].username} and ${typingUsers[1].username}`;
    }
    return `${typingUsers.length} people`;
  }, [typingUsers]);

  const { data, isLoading, refetch } = useGetGroupHistoryQuery(
    { limit: GROUP_HISTORY_LIMIT },
    {
      skip: !historySubscriptionActive,
      ...(historySubscriptionActive
        ? { pollingInterval: GROUP_HISTORY_POLL_MS }
        : {}),
    },
  );

  // last-seen read receipts — polled alongside history (and refetched on send via the
  // shared AgentGroupChat tag invalidation)
  const { data: receipts } = useGetGroupReceiptsQuery(undefined, {
    skip: !historySubscriptionActive,
    ...(historySubscriptionActive
      ? { pollingInterval: GROUP_HISTORY_POLL_MS }
      : {}),
  });

  // reactions — polled alongside history (+ live SSE deltas inside the query), and refetched
  // when a new group message invalidates the AgentGroupChat tag
  const { data: reactionsData } = useGetGroupReactionsQuery(undefined, {
    skip: !historySubscriptionActive,
    ...(historySubscriptionActive
      ? { pollingInterval: GROUP_HISTORY_POLL_MS }
      : {}),
  });
  const [reactToMessage] = useReactGroupMessageMutation();

  const [sendMessage, { isLoading: isSending }] = useSendGroupMessageMutation();
  const [sendVoice, { isLoading: isSendingVoice }] = useSendVoiceMessageMutation();
  const [recordHeard] = useRecordVoiceHeardMutation();
  const [voiceMode, setVoiceMode] = useState(false);
  const [text, setText] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const messages = useMemo(() => data?.messages ?? [], [data]);

  // listen-state for the voice notes currently in view (hear-once + one replay)
  const voiceMids = useMemo(
    () => messages.filter((m) => m.voice && m.mid).map((m) => m.mid),
    [messages],
  );
  const { data: voiceState } = useGetVoiceListenStateQuery(voiceMids, {
    skip: voiceMids.length === 0,
  });
  const voiceMaxPlays = voiceState?.maxPlays ?? 2;

  // @-mention candidates: who's online (presence) + anyone seen in history + the bot. dedupe
  // is handled by MentionTextarea.
  const mentionCandidates = useMemo(() => {
    const out: string[] = ["sitebot"];
    for (const u of presenceData?.users ?? []) out.push(u.username);
    for (const m of messages) if (m.sender.type === "user") out.push(m.sender.username);
    return out;
  }, [presenceData?.users, messages]);

  // group reactions by the message id (mid) they target
  const reactionsByMid = useMemo(() => {
    const map = new Map<string, ReactionGroup[]>();
    for (const r of reactionsData?.reactions ?? []) {
      const arr = map.get(r.mid) ?? [];
      arr.push(r);
      map.set(r.mid, arr);
    }
    return map;
  }, [reactionsData]);

  // while the group chat is on screen, keep the server read cursor advanced so
  // seen messages don't re-surface as unread on another browser/device
  const [markChatRead] = useMarkChatReadMutation();
  const lastMarkedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!historySubscriptionActive || !isLoggedIn) return;
    if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
    if (messages.length === 0) return;
    const latest = messages[messages.length - 1].createdAt;
    if (lastMarkedRef.current && latest <= lastMarkedRef.current) return;
    lastMarkedRef.current = latest;
    markChatRead({ group: latest });
  }, [historySubscriptionActive, isLoggedIn, messages, markChatRead]);

  const {
    scrollRef,
    onScroll,
    scrollToBottom,
    pendingNewCount,
  } = useAgentChatScroll(messages.length, typingLabel ?? "");

  const prevSubscriptionActiveRef = useRef(false);
  useLayoutEffect(() => {
    if (!historySubscriptionActive) {
      prevSubscriptionActiveRef.current = false;
      return;
    }
    const becameVisible = !prevSubscriptionActiveRef.current;
    prevSubscriptionActiveRef.current = true;
    if (becameVisible && messages.length > 0) {
      scrollToBottom("auto");
    }
  }, [historySubscriptionActive, messages.length, scrollToBottom]);

  const vItems: VirtualizedChatItem[] = useMemo(
    () =>
      messages.map((msg) => {
        const isAgent = msg.sender.type === "agent";
        const isSelf = !isAgent && msg.sender.username === user.username;
        return {
          id: msg.mid,
          mid: msg.mid,
          content: msg.content,
          sender: isAgent ? "sitebot" : msg.sender.username,
          senderType: isAgent ? "agent" : isSelf ? "self" : "user",
          timestamp: msg.createdAt,
          voice: msg.voice,
        };
      }),
    [messages, user.username],
  );

  // map each viewer (other than the current user) to the newest message they've read, so
  // their emoji renders under exactly that bubble (Messenger last-seen style)
  const receiptsByMessageId = useMemo(() => {
    const map = new Map<string, ReceiptViewer[]>();
    if (!receipts || messages.length === 0) return map;
    for (const r of receipts) {
      if (!r.groupLastReadAt) continue;
      if (r.username === user.username) continue; // don't show your own receipt
      const readAt = new Date(r.groupLastReadAt).getTime();
      let targetId: string | null = null;
      for (const m of messages) {
        if (new Date(m.createdAt).getTime() <= readAt) targetId = m.mid;
        else break;
      }
      if (!targetId) continue;
      const arr = map.get(targetId) ?? [];
      arr.push({ username: r.username, userEmoji: r.userEmoji });
      map.set(targetId, arr);
    }
    return map;
  }, [receipts, messages, user.username]);

  // delivered/read state for the current user's own messages, derived from other members'
  // delivered/read cursors (read wins over delivered)
  const ownStatusByMessageId = useMemo(() => {
    const map = new Map<string, "delivered" | "read">();
    if (!receipts) return map;
    const others = receipts.filter((r) => r.username !== user.username);
    for (const m of messages) {
      const isSelf = m.sender.type === "user" && m.sender.username === user.username;
      if (!isSelf) continue;
      const t = new Date(m.createdAt).getTime();
      const read = others.some(
        (r) => r.groupLastReadAt && new Date(r.groupLastReadAt).getTime() >= t,
      );
      if (read) {
        map.set(m.mid, "read");
        continue;
      }
      const delivered = others.some(
        (r) => r.groupDeliveredAt && new Date(r.groupDeliveredAt).getTime() >= t,
      );
      if (delivered) map.set(m.mid, "delivered");
    }
    return map;
  }, [receipts, messages, user.username]);

  const handleTyping = useCallback(() => {
    const now = Date.now();
    if (now - lastTypingRef.current > TYPING_THROTTLE_MS) {
      lastTypingRef.current = now;
      sendTyping({ chatType: "group" });
    }
  }, [sendTyping]);

  const handleSend = async () => {
    if (!text.trim() || isSending || !isLoggedIn) return;
    const msg = text.trim();
    setText("");
    try {
      await sendMessage({ text: msg }).unwrap();
      refetch();
    } catch {
      // keep text cleared; user can retry
    }
    inputRef.current?.focus();
  };

  const handleSendVoice = async (audio: Blob, durationMs: number) => {
    try {
      await sendVoice({ audio, target: "group", durationMs }).unwrap();
      setVoiceMode(false);
      refetch();
    } catch (err) {
      const msg =
        (err as { data?: { error?: string } })?.data?.error ?? "couldn't send the voice message";
      toast.error(msg, { position: "bottom-right" });
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0 bg-paper">
      <div className="flex items-center justify-between gap-2 px-4 py-2 border-b-2 border-ink bg-white/50 max-[700px]:px-3 max-[700px]:py-1.5">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className="font-bold text-sm shrink-0 max-[700px]:text-xs">general</span>
          <AgentStatusBadge />
        </div>
        <span className="text-xs text-gray-400 shrink-0 max-[700px]:text-[10px]">
          open channel
        </span>
      </div>

      <PresenceBar chatType="group" active={historySubscriptionActive} />

      <div className="relative flex-1 flex flex-col min-h-0">
        {historySubscriptionActive && isLoading && !data ? (
          <ChatMessagesSkeleton />
        ) : messages.length === 0 ? (
          <div className="flex-1 overflow-y-auto p-4 text-center text-gray-400 text-sm max-[700px]:text-xs py-8">
            no messages yet — start the conversation!
          </div>
        ) : (
          <>
            <VirtualizedMessageList
              scrollRef={scrollRef}
              onScroll={onScroll}
              items={vItems}
              renderItem={(item) => (
                <ChatMessage
                  sender={item.sender}
                  senderType={item.senderType}
                  content={item.content}
                  timestamp={item.timestamp}
                  status={
                    item.senderType === "self"
                      ? ownStatusByMessageId.get(item.id)
                      : undefined
                  }
                  voice={item.voice}
                  voicePlays={item.mid ? voiceState?.states[item.mid]?.plays ?? 0 : 0}
                  voiceMaxPlays={voiceMaxPlays}
                  onPlayVoice={item.mid ? () => recordHeard({ voiceId: item.mid as string }) : undefined}
                />
              )}
              renderReactions={(item) =>
                item.mid ? (
                  <MessageReactions
                    reactions={reactionsByMid.get(item.mid) ?? []}
                    onToggle={(emoji) => reactToMessage({ mid: item.mid as string, emoji })}
                    align={item.senderType === "self" ? "end" : "start"}
                  />
                ) : null
              }
              renderReceipts={(item) => {
                const viewers = receiptsByMessageId.get(item.id);
                return viewers && viewers.length > 0 ? (
                  <ReadReceipts viewers={viewers} />
                ) : null;
              }}
              footer={
                typingLabel ? (
                  <div className="px-4 pb-3 pt-1 max-[700px]:px-3 max-[700px]:pb-2">
                    <TypingIndicator who={typingLabel} />
                  </div>
                ) : undefined
              }
            />
            <NewMessagesIndicator
              count={pendingNewCount}
              onJump={() => scrollToBottom("smooth")}
            />
          </>
        )}
      </div>

      <div className="border-t-2 border-ink p-3 bg-white/50 max-[700px]:p-2 shrink-0">
        {!isLoggedIn ? (
          <div className="text-center text-gray-400 text-sm max-[700px]:text-xs py-2">
            log in to join the conversation
          </div>
        ) : voiceMode ? (
          <div className="flex items-center gap-2">
            <VoiceRecorder
              onSend={handleSendVoice}
              onCancel={() => setVoiceMode(false)}
              sending={isSendingVoice}
              iosTapOverlay
            />
          </div>
        ) : (
          <form
            className="flex gap-2 items-end"
            onSubmit={(e) => {
              e.preventDefault();
              handleSend();
            }}
          >
            <Button
              type="button"
              size="sm"
              variant="ghost"
              iosTapOverlay
              onClick={() => setVoiceMode(true)}
              className="shrink-0 max-[700px]:w-9 max-[700px]:px-0"
              aria-label="record a voice message"
              title="record a voice message"
            >
              <Mic className="w-4 h-4" />
            </Button>
            <MentionTextarea
              ref={inputRef}
              value={text}
              onChange={(v) => {
                setText(v);
                if (v.trim()) handleTyping();
              }}
              onSubmit={handleSend}
              placeholder="message #general... (@ to mention)"
              maxLength={2000}
              disabled={isSending}
              mentionCandidates={mentionCandidates}
            />
            <ChatSendButton disabled={!text.trim() || isSending} loading={isSending} />
          </form>
        )}
      </div>
    </div>
  );
}
